import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldOption } from '../../types/protocol';

interface CreateFieldDef {
  field: string;
  label: string;
  type: string;
  required?: boolean;
}

interface PendingCreate {
  [key: string]: string;
}

interface SelectManyFieldProps {
  field: string;
  options: FieldOption[];
  defaultValue?: Array<string | number>;
  help?: string;
  allowSelectAll?: boolean;
  allowCreate?: boolean;
  createLabel?: string;
  createFields?: CreateFieldDef[];
  minSelections?: number;
  searchable?: boolean;
  onSubmit: (value: unknown) => void;
}

export function SelectManyField({
  field: _field,
  options,
  defaultValue,
  help,
  allowSelectAll,
  allowCreate,
  createLabel,
  createFields,
  minSelections,
  searchable,
  onSubmit,
}: SelectManyFieldProps): React.ReactElement {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string | number>>(new Set(defaultValue ?? []));
  const [filter, setFilter] = useState('');
  const [pendingCreates, setPendingCreates] = useState<PendingCreate[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormValues, setCreateFormValues] = useState<Record<string, string>>({});
  const optionsRef = useRef<HTMLDivElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!filter.trim()) return options;
    const lower = filter.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(lower) || o.description?.toLowerCase().includes(lower),
    );
  }, [options, filter]);

  const allSelected = filteredOptions.length > 0 && filteredOptions.every((o) => selected.has(o.value));

  const toggle = useCallback((id: string | number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const o of filteredOptions) next.delete(o.value);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const o of filteredOptions) next.add(o.value);
        return next;
      });
    }
  }, [allSelected, filteredOptions]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const existing = Array.from(selected);
      if (pendingCreates.length > 0) {
        onSubmit({ existing, create: pendingCreates });
      } else {
        onSubmit(existing);
      }
    },
    [selected, pendingCreates, onSubmit],
  );

  const handleAddCreate = useCallback(() => {
    if (!createFields) return;
    const allFilled = createFields
      .filter((f) => f.required)
      .every((f) => createFormValues[f.field]?.trim());
    if (!allFilled) return;

    const newItem: PendingCreate = {};
    for (const f of createFields) {
      newItem[f.field] = createFormValues[f.field]?.trim() ?? '';
    }
    setPendingCreates((prev) => [...prev, newItem]);
    setCreateFormValues({});
    setShowCreateForm(false);
  }, [createFields, createFormValues]);

  const removePendingCreate = useCallback((index: number) => {
    setPendingCreates((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const totalSelected = selected.size + pendingCreates.length;
  const isValid = !minSelections || totalSelected >= minSelections;

  // Enter key on a focused checkbox submits the form
  const handleOptionKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isValid) {
          const existing = Array.from(selected);
          if (pendingCreates.length > 0) {
            onSubmit({ existing, create: pendingCreates });
          } else {
            onSubmit(existing);
          }
        }
      }
    },
    [isValid, selected, pendingCreates, onSubmit],
  );

  // Arrow key navigation in options list
  const handleOptionsKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const container = optionsRef.current;
      if (!container) return;
      const items = Array.from(container.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));
      const idx = items.findIndex((el) => el === document.activeElement || el.parentElement === document.activeElement);
      let next = idx;
      if (e.key === 'ArrowDown') next = Math.min(idx + 1, items.length - 1);
      else next = Math.max(idx - 1, 0);
      items[next]?.focus();
    },
    [],
  );

  // Auto-focus the first checkbox on mount
  useEffect(() => {
    const container = optionsRef.current;
    if (!container) return;
    const first = container.querySelector<HTMLInputElement>('input[type="checkbox"]');
    first?.focus();
  }, []);

  // Focus create input when form opens
  useEffect(() => {
    if (showCreateForm && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [showCreateForm]);

  return (
    <form className="sp-field sp-field-select-many" onSubmit={handleSubmit}>
      {(searchable || options.length > 8) && (
        <input
          className="sp-field-filter"
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('fields.search')}
        />
      )}
      {allowSelectAll && (
        <label className="sp-option sp-option-all">
          <input type="checkbox" checked={allSelected} onChange={toggleAll} />
          <span className="sp-option-label">
            {allSelected ? t('fields.deselectAll') : t('fields.selectAll')}
          </span>
        </label>
      )}
      <div
        className="sp-field-options sp-field-options-scroll"
        ref={optionsRef}
        onKeyDown={handleOptionsKeyDown}
      >
        {filteredOptions.map((opt) => (
          <label key={opt.value} className={`sp-option${selected.has(opt.value) ? ' sp-option-selected' : ''}`}>
            <input
              type="checkbox"
              checked={selected.has(opt.value)}
              onChange={() => toggle(opt.value)}
              onKeyDown={handleOptionKeyDown}
            />
            <span className="sp-option-label">{opt.label}</span>
            {opt.description && <span className="sp-option-desc">{opt.description}</span>}
          </label>
        ))}
        {filteredOptions.length === 0 && (
          <div className="sp-option-empty">{t('fields.noMatches')}</div>
        )}
      </div>

      {/* Pending creates shown as chips */}
      {pendingCreates.length > 0 && (
        <div className="sp-pending-creates">
          {pendingCreates.map((item, i) => {
            const displayName = Object.values(item).filter(Boolean).join(' - ');
            return (
              <span key={i} className="sp-pending-chip">
                {displayName}
                <button type="button" className="sp-pending-chip-remove" onClick={() => removePendingCreate(i)}>
                  &times;
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Inline create form */}
      {allowCreate && createFields && !showCreateForm && (
        <button
          type="button"
          className="sp-create-btn"
          onClick={() => setShowCreateForm(true)}
        >
          {createLabel ?? t('fields.createNew')}
        </button>
      )}
      {showCreateForm && createFields && (
        <div className="sp-create-form">
          {createFields.map((cf) => (
            <input
              key={cf.field}
              ref={cf === createFields[0] ? createInputRef : undefined}
              className="sp-field-input sp-create-input"
              type={cf.type === 'number' ? 'number' : 'text'}
              value={createFormValues[cf.field] ?? ''}
              onChange={(e) => setCreateFormValues((prev) => ({ ...prev, [cf.field]: e.target.value }))}
              placeholder={cf.label}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCreate();
                }
                if (e.key === 'Escape') {
                  setShowCreateForm(false);
                }
              }}
            />
          ))}
          <div className="sp-create-form-actions">
            <button type="button" className="sp-create-add-btn" onClick={handleAddCreate}>
              {t('fields.add')}
            </button>
            <button type="button" className="sp-create-cancel-btn" onClick={() => setShowCreateForm(false)}>
              {t('fields.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="sp-field-footer">
        <span className="sp-selection-count">
          {t('fields.selectedCount', { selected: totalSelected, total: options.length + pendingCreates.length })}
        </span>
        {help && <span className="sp-field-help">{help}</span>}
      </div>
      <button className="sp-field-submit" type="submit" disabled={!isValid}>
        {minSelections && !isValid ? t('fields.submitMin', { min: minSelections }) : t('fields.submit')}
      </button>
    </form>
  );
}
